import React from "react";
import { FormatWithCommas } from "@helpers/helpers";
import { ONE_VALUE } from "@utils/constants";
import { FIRST_ELEMENT, SECOND_ELEMENT, TOOLTIP_TIMEOUT } from "./constants";
import {
  createMismatchesColumn,
  createMatchingSourceColumn,
  createBusinessDayColumn,
  createCreationDateColumn,
  createRecordMatchingKeysColumn,
  createSharedChildrenColumns,
} from "../shared/columnDefinitions";
import { Database, MapPin, CircleDollarSign, Calendar } from "lucide-react";

export const getColumns = (translate) => [
  createMismatchesColumn(translate),
  createMatchingSourceColumn(translate),
  createBusinessDayColumn(translate),
  createCreationDateColumn(translate),
  createRecordMatchingKeysColumn(translate),
  {
    accessorKey: "SourceAmount",
    header: translate("outstanding-records.columns.SourceAmount"),
    size: 70,
    sortable: true,
    canPin: true,
    canFilter: false,
    filterType: "integer",
    operator: "EQ",
    processFilterValue: (value, row) => {
      return row.SourceAmount.toString();
    },
    cellActionCheck: (value) => !!value,
    cell: ({ row }) => {
      return (
        row.original.SourceAmount && (
          <span className="flex flex-col items-center justify-center group font-normal">
            <span>{`${row.original.Currency} ${FormatWithCommas(
              row.original.SourceAmount
            )}`}</span>
            <span className="font-normal text-xs">{`${
              row.original.SourceRecords.length
            } ${
              row.original.SourceRecords.length === ONE_VALUE
                ? translate("shared.record")
                : translate("shared.records")
            }`}</span>
          </span>
        )
      );
    },
  },
  {
    accessorKey: "DestinationAmount",
    header: translate("outstanding-records.columns.DestinationAmount"),
    size: 60,
    sortable: true,
    canPin: true,
    canFilter: false,
    filterType: "integer",
    operator: "EQ",
    filterCheck: (value) => {
      return value !== null;
    },
    cellActionCheck: (value) => !!value,
    cell: ({ row }) => {
      return (
        row.original.DestinationAmount && (
          <span className="flex flex-col items-center justify-center group font-normal">
            <span>{`${row.original.Currency} ${FormatWithCommas(
              row.original.DestinationAmount
            )}`}</span>
            <span className="font-normal text-xs">{`${
              row.original.DestinationRecords.length
            } ${
              row.original.DestinationRecords.length === ONE_VALUE
                ? translate("shared.record")
                : translate("shared.records")
            }`}</span>
          </span>
        )
      );
    },
  },
];

export const getChildrenColumns = (
  translate,
  setCurrentTooltip,
  TOOLTIP_TIMEOUT,
  popupOpen,
  setPopupOpen
) => {
  const sharedColumns = createSharedChildrenColumns(
    translate,
    setCurrentTooltip
  );
  return sharedColumns;
};

export const getFilters = (translate, getDynamicFilters) => [
  {
    mainFilters: [
      {
        key: "ReconciliationStreamName",
        displayName: translate("outstanding-records.filters.Reconciliations"),
        icon: () => <Database size={18} />,
        components: [
          {
            name: translate("outstanding-records.filters.Reconciliations"),
            type: "title",
            key: "title",
          },
          {
            name: translate("outstanding-records.filters.RecordReference"),
            type: "searchInput",
            key: "RecordReference",
            filterType: "string",
            operator: "EQ",
          },
          {
            name: translate(
              "outstanding-records.filters.ReconciliationStreamName"
            ),
            type: "checkboxGroup",
            key: "ReconciliationStreamTag",
            values: getDynamicFilters("ReconciliationStreamTag"),
            filterType: "checkbox",
            operator: "EQ",
          },
        ],
      },
      {
        key: "AgingTierTag",
        displayName: translate("outstanding-records.filters.AgingTier"),
        icon: () => <Database size={18} />,
        components: [
          {
            name: translate("outstanding-records.filters.AgingTier"),
            type: "title",
            key: "title",
          },
          {
            name: translate("outstanding-records.filters.AgingTier"),
            type: "checkboxGroup",
            key: "AgingTierTag",
            values: getDynamicFilters("AgingTierTag"),
            filterType: "checkbox",
            operator: "EQ",
          },
        ],
      },
      {
        key: "MismatchTag",
        displayName: translate("outstanding-records.filters.Mismatch"),
        icon: () => <Database size={18} />,
        components: [
          {
            name: translate("outstanding-records.filters.Mismatch"),
            type: "title",
            key: "title",
          },
          {
            name: translate("outstanding-records.filters.Mismatch"),
            type: "checkboxGroup",
            key: "MismatchTag",
            values: getDynamicFilters("MismatchTag"),
            filterType: "checkbox",
            operator: "EQ",
          },
          {
            name: translate("outstanding-records.filters.MismatchSeverity"),
            type: "checkboxGroup",
            key: "MismatchSeverityTag",
            values: getDynamicFilters("MismatchSeverityTag"),
            filterType: "checkbox",
            operator: "EQ",
          },
        ],
      },
      {
        key: "Location",
        displayName: translate("outstanding-records.filters.Location"),
        icon: () => <MapPin size={18} />,
        components: [
          {
            name: translate("outstanding-records.filters.Location"),
            type: "title",
            key: "title",
          },
          {
            name: translate("outstanding-records.filters.Area"),
            type: "checkboxGroup",
            key: "AreaId",
            values: getDynamicFilters("AreaId"),
            filterType: "checkbox",
            operator: "EQ",
          },
          {
            name: translate("outstanding-records.filters.Outlet"),
            type: "checkboxGroup",
            key: "OutletId",
            values: getDynamicFilters("OutletId"),
            filterType: "checkbox",
            operator: "EQ",
          },
          {
            name: translate("outstanding-records.filters.Terminal"),
            type: "checkboxGroup",
            key: "TransactionTerminalCode",
            values: getDynamicFilters("TransactionTerminalCode"),
            filterType: "checkbox",
            operator: "EQ",
          },
        ],
      },
      {
        key: "Amounts",
        displayName: translate("outstanding-records.filters.Amounts"),
        icon: () => <CircleDollarSign size={18} />,
        components: [
          {
            name: translate("outstanding-records.filters.Amounts"),
            type: "title",
            key: "title",
          },
          {
            name: translate("outstanding-records.filters.SourceAmount"),
            type: "numberInput",
            key: "SourceAmount",
            filterType: "amount",
            inputType: "number",
          },
          {
            name: translate("outstanding-records.filters.DestinationAmount"),
            type: "numberInput",
            key: "DestinationAmount",
            filterType: "amount",
            inputType: "number",
          },
        ],
      },
      {
        key: "dates",
        displayName: translate("outstanding-records.filters.dates"),
        icon: () => <Calendar size={18} />,
        components: [
          {
            name: translate("outstanding-records.filters.PleaseSelectDate"),
            type: "title",
            key: "title",
          },
          {
            name: translate("outstanding-records.filters.CreationDate"),
            type: "dateInput",
            inputType: "date",
            key: "CreationDate",
            placeHolder: translate("outstanding-records.filters.CreationDate"),
            prefix: "",
            filterType: "date",
          },
          {
            name: translate("outstanding-records.filters-display.BusinessDay"),
            type: "dateInput",
            inputType: "date",
            key: "BusinessDay",
            placeHolder: translate(
              "outstanding-records.filters-display.BusinessDay"
            ),
            prefix: "",
            filterType: "date",
          },
        ],
      },
    ],
  },
];

export const getSelectedFiltersDisplay = (translate) => ({
  ReconciliationStreamTag: translate(
    "outstanding-records.filters-display.ReconciliationStreamTag"
  ),
  AgingTierTag: translate("outstanding-records.filters-display.AgingTierTag"),
  MismatchTag: translate("outstanding-records.filters-display.MismatchTag"),
  MismatchSeverityTag: translate(
    "outstanding-records.filters-display.MismatchSeverityTag"
  ),
  AreaId: translate("outstanding-records.filters-display.AreaId"),
  OutletId: translate("outstanding-records.filters-display.OutletId"),
  TransactionTerminalCode: translate(
    "outstanding-records.filters-display.TransactionTerminalCode"
  ),
  SourceAmount: translate("outstanding-records.filters-display.SourceAmount"),
  DestinationAmount: translate(
    "outstanding-records.filters-display.DestinationAmount"
  ),
  CreationDate: translate("outstanding-records.filters-display.CreationDate"),
  BusinessDay: translate("outstanding-records.filters-display.BusinessDay"),
  RecordReference: translate(
    "outstanding-records.filters-display.RecordReference"
  ),
});

export const PINNING_INITIAL_STATE = ["pin"];
